/**
 * Whether a listing's stored URL may be printed in an email to a buyer as
 * "Listing page: …" — the ONE rule the composer, the single intro's toggle and
 * the listing-pages section preview all read.
 *
 * <p>🔴 <b>Only an absolute `https://` URL qualifies, and the value comes back
 * UNALTERED</b> (trimmed, nothing else). `Listing.rawUrl` is whatever a
 * specialist pasted — blank, relative, `http:`, `javascript:`, or prose that
 * contains a link — and none of those reaches a buyer. The host must be letters,
 * digits, hyphens and dots with an optional port (so no userinfo such as
 * `https://phoenix.example@evil.test/`, which names a different host from the
 * one a reader sees first), and every character must be printable ASCII with no
 * whitespace, so the line cannot wrap or split.</p>
 *
 * <p>This is NOT `safeHref`. That one decides whether a string is safe to put
 * in an `href` on screen and accepts `http:`; this one decides what Phoenix may
 * print in an email under its own name, which is a narrower question.</p>
 *
 * <p>The server twin is `PhoenixCapital.Core/Common/ListingPageUrl.cs`, and it
 * is the one that decides what is sent. Nothing imports across the language
 * boundary, so the two are held together by the shared fixture
 * `tests/fixtures/listing-page-url-cases.json`, read by
 * `listingPageUrl.test.ts` here and `ListingPageUrlParityTests.cs` there.</p>
 */

/** The longest URL that qualifies. */
export const LISTING_PAGE_URL_MAX_LENGTH = 2048;

const SHAPE =
  /^https:\/\/[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*(?::[0-9]{1,5})?(?:[/?#][\x21-\x7E]*)?$/i;

/** The URL to print, or null when it does not qualify. */
export function qualifyListingPageUrl(rawUrl: string | null | undefined): string | null {
  if (rawUrl == null) return null;
  const trimmed = rawUrl.trim();
  if (trimmed.length === 0 || trimmed.length > LISTING_PAGE_URL_MAX_LENGTH) return null;

  for (let i = 0; i < trimmed.length; i += 1) {
    const code = trimmed.charCodeAt(i);
    if (code < 0x21 || code > 0x7e) return null;
  }

  return SHAPE.test(trimmed) ? trimmed : null;
}

/**
 * The message and, after it, the listing-page section — what the buyer reads
 * above the signature. The client twin of the server's
 * `BuyerCombinedIntroEmailService.FullBody`, which both intro emails send
 * through, so a preview shows exactly the text a send or a copy will carry.
 */
export function withListingPages(body: string, section: string | null | undefined): string {
  return section == null ? body : `${body.trimEnd()}\n\n${section}`;
}

/**
 * The inverse, for a message that already went out: the part the consultant
 * WROTE, without the section the system added after it. Used to ask "has the
 * draft changed since this was sent?" about the message rather than about the
 * message plus a line nobody typed. A body that does not end with the section
 * comes back unchanged.
 */
export function withoutListingPages(sent: string, section: string | null | undefined): string {
  if (section == null) return sent;
  const suffix = `\n\n${section}`;
  return sent.endsWith(suffix) ? sent.slice(0, sent.length - suffix.length) : sent;
}
